import { usePointedTime } from 'utils/components/TimeSeriesChart/timepoint';
import React, { useContext } from 'react';
import AppContext from 'app/AppContext';

import classNames from 'classnames';

import './VolumeCurveChartLegends.scss';

import { WidgetTimeSeriesLegend } from 'widgets/WidgetTimeSeriesLegend/WidgetTimeSeriesLegend';
import { usePortfolioPlLegendStatus } from 'widgets/portfolioPl/chart/hooks';
import { VolumeCurveChartBucketedDataItem } from 'widgets/volumeCurve/components/chart/chartData.ts';
import { useVolumeCurveDimensions } from 'widgets/volumeCurve/components/dimensions.ts';
import { NumberFormat } from '@instinet-analytics/core-lib';
import { useVolumeCurveConfigSlice } from 'widgets/volumeCurve/store/configuration/configSlice.ts';
import { VolumeCurveLegendsConfig } from 'widgets/volumeCurve/store/configuration/VolumeCurveConfiguration.ts';
import { useSelectLocked } from 'layoutRenderer/store/selectors.ts';
import {
    auctionTooltip,
    cumulativeHistoricalVolumeTooltip,
    cumulativeTradedVolumeTooltip,
    historicalVolumeTooltip,
    tradedVolumeTooltip,
} from 'widgets/volumeCurve/components/help/volumeCurveLegendTooltip.tsx';

export interface VolumeCurveChartLegendsProps {
    containerLeft: number;
    data: VolumeCurveChartBucketedDataItem[];
}

export function VolumeCurveChartLegends(props: VolumeCurveChartLegendsProps) {
    const { containerLeft, data } = props;
    const time = usePointedTime('VolumeCurveChartLegends');
    const { numberFormat } = useContext(AppContext);
    const { legendsMarginTop } = useVolumeCurveDimensions();
    const isLocked = useSelectLocked();

    const {
        config: {
            hideTradedVolumeLegend,
            hideAuctionVolumeLegend,
            hideHistoricalVolumeExcludingAuctionLegend,
            hideCumulativeHistoricalVolumeLegend,
            hideCumulativeTradedVolumeLegend,
        },
    } = useVolumeCurveConfigSlice();

    const {
        tradedVolume,
        auctionVolume,
        historicalVolumeExcludingAuction,
        cumulativeHistoricalVolume,
        cumulativeTradedVolume,
    } = deriveLegendData(time, data, numberFormat);

    const { hideLeavesLegends } = usePortfolioPlLegendStatus();
    if (hideLeavesLegends) {
        return <></>;
    }

    const showTradedVolumeLegend = !isLocked || !hideTradedVolumeLegend;
    const showAuctionVolumeLegend = !isLocked || !hideAuctionVolumeLegend;
    const showHistoricalVolumeExcludingAuctionLegend =
        !isLocked || !hideHistoricalVolumeExcludingAuctionLegend;
    const showCumulativeHistoricalVolumeLegend =
        !isLocked || !hideCumulativeHistoricalVolumeLegend;
    const showCumulativeTradedVolumeLegend =
        !isLocked || !hideCumulativeTradedVolumeLegend;

    return (
        <div
            className={classNames('VolumeCurveChartLegends', 'legend-group')}
            style={{
                marginTop: legendsMarginTop + 'px',
            }}
        >
            <div className="legend-group">
                {showHistoricalVolumeExcludingAuctionLegend && (
                    <WidgetTimeSeriesLegend<VolumeCurveLegendsConfig>
                        className={classNames(
                            'VolumeCurveChartLegend',
                            'historical'
                        )}
                        label={'Hist'}
                        hideSeriesPropertyName={
                            'hideHistoricalVolumeExcludingAuction'
                        }
                        value={historicalVolumeExcludingAuction}
                        containerLeft={containerLeft}
                        tooltip={historicalVolumeTooltip}
                    />
                )}
                {showCumulativeHistoricalVolumeLegend && (
                    <WidgetTimeSeriesLegend<VolumeCurveLegendsConfig>
                        className={classNames(
                            'VolumeCurveChartLegend',
                            'historical'
                        )}
                        label={'C Hist'}
                        hideSeriesPropertyName={
                            'hideCumulativeHistoricalVolume'
                        }
                        value={cumulativeHistoricalVolume}
                        containerLeft={containerLeft}
                        tooltip={cumulativeHistoricalVolumeTooltip}
                    />
                )}

                {showAuctionVolumeLegend && (
                    <WidgetTimeSeriesLegend<VolumeCurveLegendsConfig>
                        className={classNames(
                            'VolumeCurveChartLegend',
                            'auction'
                        )}
                        label={'Auctions'}
                        value={auctionVolume}
                        containerLeft={containerLeft}
                        hideSeriesPropertyName={'hideAuctionVolume'}
                        tooltip={auctionTooltip}
                    />
                )}
            </div>
            <div className="legend-group">
                {showTradedVolumeLegend && (
                    <WidgetTimeSeriesLegend<VolumeCurveLegendsConfig>
                        className={classNames(
                            'VolumeCurveChartLegend',
                            'traded'
                        )}
                        label={'Traded'}
                        value={tradedVolume}
                        containerLeft={containerLeft}
                        hideSeriesPropertyName={'hideTradedVolume'}
                        tooltip={tradedVolumeTooltip}
                    />
                )}
                {showCumulativeTradedVolumeLegend && (
                    <WidgetTimeSeriesLegend<VolumeCurveLegendsConfig>
                        className={classNames(
                            'VolumeCurveChartLegend',
                            'cumulative-traded'
                        )}
                        label={'C Traded'}
                        hideSeriesPropertyName={'hideCumulativeTradedVolume'}
                        value={cumulativeTradedVolume}
                        containerLeft={containerLeft}
                        tooltip={cumulativeTradedVolumeTooltip}
                    />
                )}
            </div>
        </div>
    );
}

type VolumeCurveLegendDataValues = {
    historicalVolumeExcludingAuction?: number;
    tradedVolume?: number;
    auctionVolume?: number;
    cumulativeTradedVolume?: number;
    cumulativeHistoricalVolume?: number;
};

type VolumeCurveLegendData = {
    historicalVolumeExcludingAuction?: string;
    tradedVolume?: string;
    auctionVolume?: string;
    cumulativeTradedVolume?: string;
    cumulativeHistoricalVolume?: string;
};
export function deriveLegendData(
    time: number | undefined,
    data: VolumeCurveChartBucketedDataItem[],
    numberFormat: NumberFormat
): VolumeCurveLegendData {
    let dataValues: VolumeCurveLegendDataValues | undefined;

    if (time === undefined) {
        dataValues = getLatestWithValues(data);
    } else {
        const beforeTime = time && data.filter((d) => d.time <= time!);

        dataValues = beforeTime ? beforeTime[beforeTime.length - 1] : undefined;
    }

    if (!dataValues) {
        return {};
    }

    return {
        historicalVolumeExcludingAuction: getLegendValue(
            dataValues.historicalVolumeExcludingAuction,
            numberFormat
        ),
        tradedVolume: getLegendValue(dataValues.tradedVolume, numberFormat),
        auctionVolume: getLegendValue(dataValues.auctionVolume, numberFormat),
        cumulativeTradedVolume: getLegendValue(
            dataValues.cumulativeTradedVolume,
            numberFormat
        ),
        cumulativeHistoricalVolume: getLegendValue(
            dataValues.cumulativeHistoricalVolume,
            numberFormat
        ),
    };
}

function getLatestWithValues(
    data: VolumeCurveChartBucketedDataItem[]
): VolumeCurveLegendDataValues {
    let populatedCount = 0;
    const ret: VolumeCurveLegendDataValues = {};

    const copyNumberIfMissing = (
        key:
            | 'historicalVolumeExcludingAuction'
            | 'tradedVolume'
            | 'auctionVolume'
            | 'cumulativeTradedVolume'
            | 'cumulativeHistoricalVolume',
        item: VolumeCurveChartBucketedDataItem
    ) => {
        if (item[key] !== undefined && ret[key] === undefined) {
            ret[key] = item[key];
            populatedCount++;
        }
    };

    for (let i = data.length - 1; i >= 0; i--) {
        const item = data[i];

        copyNumberIfMissing('historicalVolumeExcludingAuction', item);
        copyNumberIfMissing('tradedVolume', item);
        copyNumberIfMissing('auctionVolume', item);
        copyNumberIfMissing('cumulativeTradedVolume', item);
        copyNumberIfMissing('cumulativeHistoricalVolume', item);

        if (populatedCount === 5) {
            return ret;
        }
    }
    return ret;
}
function getLegendValue(value: number | undefined, numberFormat: NumberFormat) {
    return value !== undefined ? numberFormat.n0(value) : undefined;
}
